import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { LoginModal } from '../components/auth/LoginModal';
import { RegisterModal } from '../components/auth/RegisterModal';
import { EmailVerificationModal } from '../components/auth/EmailVerificationModal';
import { PasswordResetModal } from '../components/auth/PasswordResetModal';
import { Button } from '../components/ui/Button';
import { useAuthStore } from '../store/authStore';
import {
  BeakerIcon,
  DocumentChartBarIcon,
  BookOpenIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  BoltIcon,
  CloudArrowUpIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';

// Unsplash royalty-free images for health theme
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1920&q=80', // Doctor with stethoscope
  labs: 'https://images.unsplash.com/photo-1579154204601-01588f351e67?w=800&q=80', // Lab test tubes
  medical: 'https://images.unsplash.com/photo-1581056771107-24ca5f033842?w=800&q=80', // Medical professional
  wellness: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80', // Fitness wellness
  office: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&q=80', // Modern healthcare
};

export const LandingPage: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleRegistrationSuccess = (email: string) => {
    setShowRegister(false);
    setVerificationEmail(email);
    setShowEmailVerification(true);
  };

  const handleEmailVerificationSuccess = () => {
    setShowEmailVerification(false);
    setShowLogin(true);
  };

  const handlePasswordResetSuccess = () => {
    setShowPasswordReset(false);
    setShowLogin(true);
  };

  const medicalDocumentsFeatures = [
    {
      icon: BeakerIcon,
      title: 'Lab Results Interpreter',
      description:
        'Understand your CBC, metabolic panels, lipid panels, and more with plain-language explanations of what each value means.',
    },
    {
      icon: DocumentChartBarIcon,
      title: 'Imaging Report Interpreter',
      description:
        'Decode X-ray, CT, MRI, and ultrasound reports. Understand findings, impressions, and what follow-up might mean.',
    },
    {
      icon: BookOpenIcon,
      title: 'Office Visit Notes Interpreter',
      description:
        'Make sense of SOAP notes, progress notes, consult notes, and discharge summaries — what was decided, what you need to do, and what to ask next.',
    },
  ];

  const healthTrackingFeatures = [
    {
      icon: ClipboardDocumentListIcon,
      title: 'Symptom Tracker',
      description:
        'Log symptoms over time, identify patterns, and generate reports to share with your healthcare provider.',
    },
    {
      icon: CalculatorIcon,
      title: 'Health Metrics Calculator',
      description:
        'Calculate BMI, BMR, body fat percentage, and other health metrics with educational context.',
    },
    {
      icon: ExclamationTriangleIcon,
      title: 'Drug Interaction Checker',
      description:
        'Check for potential interactions between medications, supplements, and foods.',
    },
  ];

  const benefits = [
    {
      icon: BoltIcon,
      title: 'Instant Understanding',
      description: 'Get clear explanations of complex medical information in seconds.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Private & Secure',
      description: 'Your health information stays private with enterprise-grade security.',
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Easy Upload',
      description: 'Paste or upload lab reports and imaging results for instant interpretation.',
    },
    {
      icon: GlobeAltIcon,
      title: '5 Languages',
      description: 'Full support for English, Spanish, French, German, and Italian.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-200/90 backdrop-blur-md border-b border-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="/logo_light.png"
                alt="Auxilio Health"
                className="h-12"
              />
            </div>
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={() => setShowLogin(true)}>
                Login
              </Button>
              <Button onClick={() => setShowRegister(true)}>Register</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-20 pb-20 px-4 sm:px-6 lg:px-8 min-h-[90vh] flex items-center">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${IMAGES.hero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/95 via-emerald-900/85 to-emerald-800/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <div className="mb-6 flex items-center">
                <span className="text-4xl sm:text-5xl font-auxilio" style={{ color: '#9CE9D1' }}>Auxilio</span>
                <img src="/caduceus.png" alt="Health" className="h-12 sm:h-16 lg:h-20 w-12 sm:w-16 lg:w-20 drop-shadow-lg" />
                <span className="text-4xl sm:text-5xl font-auxilio" style={{ color: '#9CE9D1' }}>Health</span>
              </div>

              <p className="text-xl sm:text-2xl text-emerald-100 mb-6 max-w-xl">
                Your AI-Powered Health Information Assistant
              </p>

              <p className="text-lg text-emerald-200 mb-8 max-w-lg">
                Understand your lab results, imaging reports, and medical terminology in plain language.
                Make informed decisions about your health with AI-powered explanations.
              </p>

              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Button size="lg" onClick={() => setShowRegister(true)} className="text-lg px-8 py-4">
                  Get Started Free
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={() => setShowLogin(true)}
                  className="text-lg px-8 py-4 bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Login to Your Account
                </Button>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative">
                <img
                  src={IMAGES.labs}
                  alt="Health lab analysis"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-emerald-600 text-white p-4 rounded-xl shadow-lg">
                  <p className="text-3xl font-bold">10+</p>
                  <p className="text-sm">Health Tools</p>
                </div>
                <div className="absolute -top-6 -right-6 bg-teal-600 text-white p-4 rounded-xl shadow-lg">
                  <p className="text-3xl font-bold">5</p>
                  <p className="text-sm">Languages</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Documents Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="order-2 lg:order-1">
              <img
                src={IMAGES.medical}
                alt="Medical interpretation"
                className="rounded-2xl shadow-xl"
              />
            </div>

            <div className="order-1 lg:order-2">
              <div className="flex items-center mb-4">
                <div className="bg-emerald-100 p-3 rounded-full mr-4">
                  <BeakerIcon className="h-8 w-8 text-emerald-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Medical Documents Interpretation
                </h2>
              </div>
              <p className="text-lg text-gray-600 mb-6">
                Finally understand what your test results actually mean. Our AI translates complex
                medical reports into clear, understandable language.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {medicalDocumentsFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1 border border-gray-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="bg-emerald-100 p-3 rounded-lg">
                      <feature.icon className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Health Tracking Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${IMAGES.wellness})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}
        >
          <div className="absolute inset-0 bg-gray-100/95" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="flex items-center mb-4">
                <div className="bg-teal-100 p-3 rounded-full mr-4">
                  <ClipboardDocumentListIcon className="h-8 w-8 text-teal-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Health Tracking & Tools
                </h2>
              </div>
              <p className="text-lg text-gray-600 mb-6">
                Track symptoms, calculate health metrics, and check for potential drug interactions.
                Be prepared for every doctor visit with comprehensive health data.
              </p>
            </div>

            <div>
              <img
                src={IMAGES.office}
                alt="Modern healthcare"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {healthTrackingFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1 border border-gray-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="bg-teal-100 p-3 rounded-lg">
                      <feature.icon className="h-6 w-6 text-teal-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Auxilio Health?
            </h2>
            <p className="text-xl text-gray-600">
              Built for understanding, privacy, and ease of use
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="flex justify-center mb-4">
                  <div className="bg-emerald-50 p-4 rounded-full">
                    <benefit.icon className="h-8 w-8 text-emerald-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-teal-700 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <img src="/caduceus.png" alt="Auxilio Health" className="h-20 w-20 opacity-90" />
          </div>
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Understand Your Health?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Join thousands of users who trust Auxilio Health to help them understand their medical information.
            Free to register, no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
            <Button
              size="lg"
              onClick={() => setShowRegister(true)}
              className="bg-white text-emerald-700 hover:bg-gray-50 text-lg px-8 py-4 font-semibold shadow-lg"
            >
              Create Free Account
            </Button>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => setShowLogin(true)}
              className="bg-emerald-700 text-white hover:bg-emerald-600 border-white text-lg px-8 py-4"
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center items-center space-x-2 mb-4">
            <img src="/caduceus.png" alt="Auxilio Health" className="h-8 w-8" />
            <span className="text-xl font-bold text-white">Auxilio Health</span>
          </div>
          <p className="text-gray-400 text-sm">
            AI-Powered Health Information Assistant
          </p>
          <p className="text-gray-500 text-xs mt-4 max-w-2xl mx-auto">
            Auxilio Health provides health information and educational tools. It does not constitute medical advice.
            Always consult a qualified healthcare provider for medical decisions.
          </p>
          <p className="text-gray-600 text-xs mt-4">
            Images provided by <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300">Unsplash</a>
          </p>
          <p className="text-gray-500 text-xs mt-2">
            &copy; {new Date().getFullYear()} Auxilio Health. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Modals */}
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSwitchToRegister={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
        onSwitchToPasswordReset={() => {
          setShowLogin(false);
          setShowPasswordReset(true);
        }}
      />

      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onSuccess={handleRegistrationSuccess}
      />

      <EmailVerificationModal
        isOpen={showEmailVerification}
        onClose={() => setShowEmailVerification(false)}
        onSuccess={handleEmailVerificationSuccess}
        email={verificationEmail}
      />

      <PasswordResetModal
        isOpen={showPasswordReset}
        onClose={() => setShowPasswordReset(false)}
        onSuccess={handlePasswordResetSuccess}
        onSwitchToLogin={() => {
          setShowPasswordReset(false);
          setShowLogin(true);
        }}
      />
    </div>
  );
};
